import React, { Suspense, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, Grid, Sky, ContactShadows } from '@react-three/drei';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useStorefront3DStore } from '../store/useStorefront3DStore';
import StoreFloor from './3d/StoreFloor';
import ProductDisplay from './3d/ProductDisplay';
import ShelfUnit from './3d/ShelfUnit';
import Lighting from './3d/Lighting';
import GeneratedModel from './3d/GeneratedModel';

const LoadingFallback = () => (
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2
    }}
  >
    <CircularProgress />
    <Typography variant="body2" color="text.secondary">
      Loading 3D Environment...
    </Typography>
  </Box>
);

const Scene = () => {
  const { storeLayout, selectedItems, generatedModels, viewerSettings } = useStorefront3DStore();
  
  return (
    <>
      {/* Lighting */}
      <Lighting />
      
      {/* Environment */}
      <Environment preset={viewerSettings.environment || 'warehouse'} />
      <Sky sunPosition={[100, 20, 100]} />
      
      {/* Floor and Grid */}
      <StoreFloor size={storeLayout.floorSize} />
      <Grid 
        args={[storeLayout.floorSize, storeLayout.floorSize]} 
        cellSize={1} 
        cellThickness={0.5} 
        cellColor="#6f6f6f" 
        sectionSize={5} 
        sectionThickness={1} 
        sectionColor="#9d4b4b" 
        fadeDistance={30} 
        fadeStrength={1} 
        followCamera={false} 
        infiniteGrid={true}
      />
      
      {/* Contact Shadows */}
      <ContactShadows 
        position={[0, 0, 0]} 
        opacity={0.4} 
        scale={50} 
        blur={1} 
        far={10} 
        resolution={256} 
        color="#000000" 
      />
      
      {/* Store Layout Elements */}
      {storeLayout.shelves?.map((shelf, index) => (
        <ShelfUnit
          key={`shelf-${index}`}
          position={shelf.position}
          rotation={shelf.rotation}
          size={shelf.size}
          items={shelf.items}
        />
      ))}
      
      {/* Individual Product Displays */}
      {selectedItems?.map((item, index) => (
        <ProductDisplay
          key={`product-${item.id}-${index}`}
          item={item}
          position={item.position || [index * 2, 0, 0]}
        />
      ))}
      
      {/* Generated 3D Models */}
      {generatedModels?.map((model, index) => (
        <GeneratedModel
          key={`generated-${model.id}-${index}`}
          model={model}
          position={model.position || [index * 3, 0, index * 2]}
        />
      ))}
    </>
  );
};

const ViewerCanvas = () => {
  const canvasRef = useRef();
  const { viewerSettings } = useStorefront3DStore();
  
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Canvas
        ref={canvasRef}
        camera={{ 
          position: [10, 8, 10], 
          fov: 60,
          near: 0.1,
          far: 1000
        }}
        shadows
        gl={{ 
          antialias: true,
          alpha: false,
          powerPreference: "high-performance"
        }}
        style={{ background: '#f5f5f5' }}
      >
        <Suspense fallback={null}>
          <Scene />
          
          {/* Controls */}
          <OrbitControls
            enablePan={true}
            enableZoom={true}
            enableRotate={true}
            minDistance={2}
            maxDistance={50}
            maxPolarAngle={Math.PI / 2}
            target={[0, 0, 0]}
          />
        </Suspense>
      </Canvas>
      
      {/* Loading Overlay */}
      <Suspense fallback={<LoadingFallback />}>
        <div />
      </Suspense>
    </Box>
  );
};

export default ViewerCanvas;
